// GENERATED from packages/geo/geo.ts by scripts/sync-geo.mjs. Do not edit here.
/**
 * Countries, provinces and address formats for Terify. Single source of truth.
 *
 * Edit THIS file, then run `node scripts/sync-geo.mjs`. It copies the file into
 * apps/{backend,admin,storefront}/src/lib/geo.ts (each app's Docker build only sees
 * its own folder). CI fails if a copy differs from this file.
 *
 * Codes: ISO 3166-1 alpha-2 for countries (stored lowercase on Medusa addresses, shown
 * uppercase), ISO 3166-2 subdivision codes without the country prefix for provinces
 * (stored uppercase in `address.province`). Pure data + pure functions, no imports.
 */

/** Every ISO 3166-1 country Medusa ships with (XK and other user-assigned codes excluded). */
export const COUNTRY_CODES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP " +
  "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT " +
  "MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
  "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG " +
  "UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ")

/** EU member states (shortcut in country pickers). */
export const EU_COUNTRIES = "AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE".split(" ")
/** Rest of Europe, outside the EU. */
export const EUROPE_NON_EU = "AD AL AX BA BY CH FO GB GG GI IM IS JE LI MC MD ME MK NO RS SJ SM UA VA".split(" ")

const names: { of(code: string): string | undefined } | null = (() => {
  try {
    return typeof Intl !== "undefined" && "DisplayNames" in Intl ? new (Intl as any).DisplayNames(["en"], { type: "region" }) : null
  } catch {
    return null
  }
})()

const up = (c?: string | null) => (c ?? "").trim().toUpperCase()

export const isCountryCode = (code?: string | null) => COUNTRY_CODES.includes(up(code))

export function countryName(code?: string | null): string {
  const c = up(code)
  if (!c) return ""
  try {
    return names?.of(c) ?? c
  } catch {
    return c
  }
}

/** Country options for a select, sorted by name. `codes` limits the list (any case). */
export function countryOptions(codes?: string[]): { code: string; name: string }[] {
  const list = codes ? [...new Set(codes.map(up))].filter(isCountryCode) : COUNTRY_CODES
  return list.map((c) => ({ code: c.toLowerCase(), name: countryName(c) })).sort((a, b) => a.name.localeCompare(b.name))
}

type P = [code: string, name: string]
const list = (s: string): P[] => s.split("|").map((x) => x.split(":") as P)

const PROVINCES: Record<string, P[]> = {
  US: list(
    "AL:Alabama|AK:Alaska|AZ:Arizona|AR:Arkansas|CA:California|CO:Colorado|CT:Connecticut|DE:Delaware|DC:District of Columbia|FL:Florida|GA:Georgia|HI:Hawaii|ID:Idaho|IL:Illinois|IN:Indiana|IA:Iowa|KS:Kansas|KY:Kentucky|LA:Louisiana|ME:Maine|MD:Maryland|MA:Massachusetts|MI:Michigan|MN:Minnesota|MS:Mississippi|MO:Missouri|MT:Montana|NE:Nebraska|NV:Nevada|NH:New Hampshire|NJ:New Jersey|NM:New Mexico|NY:New York|NC:North Carolina|ND:North Dakota|OH:Ohio|OK:Oklahoma|OR:Oregon|PA:Pennsylvania|RI:Rhode Island|SC:South Carolina|SD:South Dakota|TN:Tennessee|TX:Texas|UT:Utah|VT:Vermont|VA:Virginia|WA:Washington|WV:West Virginia|WI:Wisconsin|WY:Wyoming|AS:American Samoa|GU:Guam|MP:Northern Mariana Islands|PR:Puerto Rico|VI:U.S. Virgin Islands|AA:Armed Forces Americas|AE:Armed Forces Europe|AP:Armed Forces Pacific"
  ),
  CA: list("AB:Alberta|BC:British Columbia|MB:Manitoba|NB:New Brunswick|NL:Newfoundland and Labrador|NS:Nova Scotia|NT:Northwest Territories|NU:Nunavut|ON:Ontario|PE:Prince Edward Island|QC:Quebec|SK:Saskatchewan|YT:Yukon"),
  AU: list("ACT:Australian Capital Territory|NSW:New South Wales|NT:Northern Territory|QLD:Queensland|SA:South Australia|TAS:Tasmania|VIC:Victoria|WA:Western Australia"),
  MX: list(
    "AGU:Aguascalientes|BCN:Baja California|BCS:Baja California Sur|CAM:Campeche|CHP:Chiapas|CHH:Chihuahua|CMX:Ciudad de México|COA:Coahuila|COL:Colima|DUR:Durango|GUA:Guanajuato|GRO:Guerrero|HID:Hidalgo|JAL:Jalisco|MEX:Estado de México|MIC:Michoacán|MOR:Morelos|NAY:Nayarit|NLE:Nuevo León|OAX:Oaxaca|PUE:Puebla|QUE:Querétaro|ROO:Quintana Roo|SLP:San Luis Potosí|SIN:Sinaloa|SON:Sonora|TAB:Tabasco|TAM:Tamaulipas|TLA:Tlaxcala|VER:Veracruz|YUC:Yucatán|ZAC:Zacatecas"
  ),
  IN: list(
    "AN:Andaman and Nicobar Islands|AP:Andhra Pradesh|AR:Arunachal Pradesh|AS:Assam|BR:Bihar|CH:Chandigarh|CT:Chhattisgarh|DH:Dadra and Nagar Haveli and Daman and Diu|DL:Delhi|GA:Goa|GJ:Gujarat|HR:Haryana|HP:Himachal Pradesh|JK:Jammu and Kashmir|JH:Jharkhand|KA:Karnataka|KL:Kerala|LA:Ladakh|LD:Lakshadweep|MP:Madhya Pradesh|MH:Maharashtra|MN:Manipur|ML:Meghalaya|MZ:Mizoram|NL:Nagaland|OR:Odisha|PY:Puducherry|PB:Punjab|RJ:Rajasthan|SK:Sikkim|TN:Tamil Nadu|TG:Telangana|TR:Tripura|UP:Uttar Pradesh|UT:Uttarakhand|WB:West Bengal"
  ),
  BR: list(
    "AC:Acre|AL:Alagoas|AP:Amapá|AM:Amazonas|BA:Bahia|CE:Ceará|DF:Distrito Federal|ES:Espírito Santo|GO:Goiás|MA:Maranhão|MT:Mato Grosso|MS:Mato Grosso do Sul|MG:Minas Gerais|PA:Pará|PB:Paraíba|PR:Paraná|PE:Pernambuco|PI:Piauí|RJ:Rio de Janeiro|RN:Rio Grande do Norte|RS:Rio Grande do Sul|RO:Rondônia|RR:Roraima|SC:Santa Catarina|SP:São Paulo|SE:Sergipe|TO:Tocantins"
  ),
  JP: list(
    "01:Hokkaido|02:Aomori|03:Iwate|04:Miyagi|05:Akita|06:Yamagata|07:Fukushima|08:Ibaraki|09:Tochigi|10:Gunma|11:Saitama|12:Chiba|13:Tokyo|14:Kanagawa|15:Niigata|16:Toyama|17:Ishikawa|18:Fukui|19:Yamanashi|20:Nagano|21:Gifu|22:Shizuoka|23:Aichi|24:Mie|25:Shiga|26:Kyoto|27:Osaka|28:Hyogo|29:Nara|30:Wakayama|31:Tottori|32:Shimane|33:Okayama|34:Hiroshima|35:Yamaguchi|36:Tokushima|37:Kagawa|38:Ehime|39:Kochi|40:Fukuoka|41:Saga|42:Nagasaki|43:Kumamoto|44:Oita|45:Miyazaki|46:Kagoshima|47:Okinawa"
  ),
  AE: list("AZ:Abu Dhabi|AJ:Ajman|DU:Dubai|FU:Fujairah|RK:Ras Al Khaimah|SH:Sharjah|UQ:Umm Al Quwain"),
}

export type FieldRule = "required" | "optional" | "none"

export type AddressFormat = {
  province: FieldRule
  provinceLabel: string
  /** When present, province must be one of these codes. */
  provinces?: { code: string; name: string }[]
  postal: FieldRule
  postalLabel: string
  postalPlaceholder?: string
  /** Order of the city / province / postal line when an address is displayed. */
  layout: "city-province-postal" | "postal-city" | "city-postal-lines" | "postal-province-city"
}

/** Countries with no postal code system. */
const NO_POSTAL = new Set(
  "AE AG AO AW BF BI BJ BO BS BW BZ CD CF CG CI CK CM DJ DM ER FJ GA GD GH GM GQ GY HK KI KM KN KP LC ML MO MR MS MW NR NU QA RW SB SC SL SO SR SS ST SY TD TF TG TK TL TO TT TV UG VU YE ZW".split(" ")
)
const POSTAL_OPTIONAL = new Set("IE PA".split(" "))

/** Lenient patterns (uppercase, single spaces) for the countries we ship to most. */
const POSTAL_PATTERNS: Record<string, [RegExp, string]> = {
  US: [/^\d{5}(-\d{4})?$/, "10001"],
  CA: [/^[A-Z]\d[A-Z] ?\d[A-Z]\d$/, "K1A 0B1"],
  GB: [/^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/, "SW1A 1AA"],
  AU: [/^\d{4}$/, "2000"],
  NZ: [/^\d{4}$/, "1010"],
  DE: [/^\d{5}$/, "10115"],
  FR: [/^\d{5}$/, "75001"],
  IT: [/^\d{5}$/, "00118"],
  ES: [/^\d{5}$/, "28001"],
  MX: [/^\d{5}$/, "06000"],
  NL: [/^\d{4} ?[A-Z]{2}$/, "1012 AB"],
  BE: [/^\d{4}$/, "1000"],
  AT: [/^\d{4}$/, "1010"],
  CH: [/^\d{4}$/, "8001"],
  DK: [/^\d{4}$/, "1050"],
  NO: [/^\d{4}$/, "0150"],
  SE: [/^\d{3} ?\d{2}$/, "111 22"],
  PL: [/^\d{2}-\d{3}$/, "00-001"],
  PT: [/^\d{4}-\d{3}$/, "1000-001"],
  IE: [/^[A-Z]\d[\dW] ?[A-Z\d]{4}$/, "D02 X285"],
  IN: [/^\d{6}$/, "110001"],
  SG: [/^\d{6}$/, "018956"],
  JP: [/^\d{3}-?\d{4}$/, "100-0001"],
  BR: [/^\d{5}-?\d{3}$/, "01000-000"],
}

const PROVINCE_LABEL: Record<string, string> = {
  US: "State", MX: "State", IN: "State", BR: "State", AU: "State/territory", CA: "Province", JP: "Prefecture", AE: "Emirate", IE: "County", GB: "County",
}
const PROVINCE_REQUIRED = new Set(["US", "CA", "AU", "MX", "IN", "BR", "JP", "AE"])
const POSTAL_LABEL: Record<string, string> = { US: "ZIP code", IN: "PIN code", IE: "Eircode", GB: "Postcode", AU: "Postcode", NZ: "Postcode" }
const LAYOUT: Record<string, AddressFormat["layout"]> = {
  US: "city-province-postal", CA: "city-province-postal", AU: "city-province-postal", IN: "city-province-postal", AE: "city-province-postal",
  GB: "city-postal-lines", IE: "city-postal-lines", JP: "postal-province-city",
}

export function addressFormat(country?: string | null): AddressFormat {
  const c = up(country)
  const provinces = PROVINCES[c]?.map(([code, name]) => ({ code, name }))
  const postal: FieldRule = NO_POSTAL.has(c) ? "none" : POSTAL_OPTIONAL.has(c) ? "optional" : "required"
  return {
    province: PROVINCE_REQUIRED.has(c) ? "required" : "optional",
    provinceLabel: PROVINCE_LABEL[c] ?? "State/province/region",
    provinces,
    postal,
    postalLabel: POSTAL_LABEL[c] ?? "Postal code",
    postalPlaceholder: POSTAL_PATTERNS[c]?.[1],
    layout: LAYOUT[c] ?? "postal-city",
  }
}

export const hasProvinceList = (country?: string | null) => !!PROVINCES[up(country)]

export function provinceName(country?: string | null, code?: string | null): string {
  const c = up(code)
  return PROVINCES[up(country)]?.find(([k]) => k === c)?.[1] ?? (code ?? "")
}

/** Uppercase + single spaces, the stored form of a postal code. */
export const normalizePostal = (v?: string | null) => (v ?? "").trim().toUpperCase().replace(/\s+/g, " ")

export function isValidPostal(country: string | null | undefined, value: string | null | undefined): boolean {
  const v = normalizePostal(value)
  if (v.length > 20) return false
  const p = POSTAL_PATTERNS[up(country)]
  return p ? p[0].test(v) : /^[A-Z0-9][A-Z0-9 -]{1,18}[A-Z0-9]$/.test(v) || /^[A-Z0-9]{2,3}$/.test(v)
}

export type AddressInput = {
  first_name?: string | null; last_name?: string | null; company?: string | null
  address_1?: string | null; address_2?: string | null; city?: string | null
  province?: string | null; postal_code?: string | null; country_code?: string | null; phone?: string | null
}

export type AddressProblem = { field: "country_code" | "province" | "postal_code"; message: string }

/** Checks country / province / postal against the country's format. Other fields are the caller's job. */
export function checkAddress(a: AddressInput, allowedCountries?: string[]): AddressProblem | null {
  const c = up(a.country_code)
  if (!isCountryCode(c)) return { field: "country_code", message: "Choose a country/region." }
  if (allowedCountries && !allowedCountries.map(up).includes(c)) return { field: "country_code", message: `We don't ship to ${countryName(c)} yet.` }
  const f = addressFormat(c)
  const province = up(a.province)
  if (f.province === "required" && !province) return { field: "province", message: `Enter a ${f.provinceLabel.toLowerCase()}.` }
  if (province && f.provinces && !f.provinces.some((p) => p.code === province)) return { field: "province", message: `Choose a ${f.provinceLabel.toLowerCase()}.` }
  const postal = normalizePostal(a.postal_code)
  if (f.postal === "required" && !postal) return { field: "postal_code", message: `Enter a ${f.postalLabel.toLowerCase()}.` }
  if (postal && f.postal !== "none" && !isValidPostal(c, postal))
    return { field: "postal_code", message: `Enter a valid ${f.postalLabel.toLowerCase()}${f.postalPlaceholder ? ` (e.g. ${f.postalPlaceholder})` : ""}.` }
  return null
}

/** Address as display lines, in the country's usual order. */
export function formatAddressLines(a: AddressInput | null | undefined, opts: { name?: boolean; phone?: boolean } = {}): string[] {
  if (!a) return []
  const f = addressFormat(a.country_code)
  // Coded provinces show their code (NY, ON), like Shopify; free-text ones as typed.
  const prov = a.province ? (f.provinces ? up(a.province) : a.province) : ""
  const join = (...xs: (string | null | undefined)[]) => xs.filter((x) => x && String(x).trim()).join(" ")
  const lines: (string | null | undefined)[] = []
  if (opts.name !== false) lines.push(join(a.first_name, a.last_name))
  lines.push(a.company, a.address_1, a.address_2)
  switch (f.layout) {
    case "city-province-postal": lines.push(join(a.city, prov, a.postal_code)); break
    case "city-postal-lines": lines.push(join(a.city, prov), a.postal_code); break
    case "postal-province-city": lines.push(join(a.postal_code, prov, a.city)); break
    default: lines.push(join(a.postal_code, a.city), prov)
  }
  lines.push(countryName(a.country_code))
  if (opts.phone !== false) lines.push(a.phone)
  return lines.map((l) => (l ?? "").trim()).filter(Boolean)
}
